type skeletonElement = HTMLElement | Document | Element;

export interface listItem {
  name: string;
  id?: number;
}

interface CardData {
  title?: string;
  subtitle?: string;
  text?: string;
  summary?: string,
  category?: listItem[];
  image?: string;
  modal_target?: string;
  link?: string;
}

function elementControl(
  element: string,
  value: string,
  skeleton: skeletonElement
): void {
  let plainText: string = value;

  const target = skeleton.querySelector<HTMLElement>(element);
  if (target) {
    const truncateWords = target.dataset.truncatewords;
    const truncateChars = target.dataset.truncatechars;
    if (truncateWords && plainText) {
      const truncatedValue = value.split(' ').slice(0, Number(truncateWords));

      plainText = value.replace(/(<([^>]+)>)/gi, "");

      if (element !== '.js-paragraph') {
        target.title = plainText;
      }

      plainText =
        plainText.length > Number(truncateWords)
          ? `${truncatedValue.join(' ')}…`
          : plainText;
    }

    if (truncateChars && plainText) {
      plainText = value.slice(0, Number(truncateChars));
    }

    plainText ? (target.innerHTML = plainText) : target.remove();
  }
}

function listControl(
  element: string,
  value: listItem[],
  skeleton: skeletonElement
): void {
  const itemList = skeleton.querySelector<HTMLElement>(element);
  if (!itemList) return;

  const list = itemList.parentElement;
  if (!list) return;

  if (value.length > 0) {
    value.forEach((el: listItem) => {
      const item = itemList.cloneNode(true) as HTMLElement;
      item.innerHTML = el.name;
      list.appendChild(item);
    });
  }
  itemList.remove();
}

function imageControl(
  element: string,
  value: string,
  skeleton: skeletonElement,
  title: string | false = false
): void {
  const img = skeleton.querySelector<HTMLImageElement>(element);
  if (img) {
    if (value) {
      img.src = value;
      if (title) {
        img.alt = title;
      }
    } else {
      img.remove();
    }
  }
}

function linkControl(
  element: string,
  value: string,
  skeleton: skeletonElement
): void {
  const links = skeleton.querySelectorAll<HTMLAnchorElement>(element);
  if (links) {
    links.forEach((el) => {
      el ? (el.href = value) : el?.remove();
    });
  }
}

function modalControl(
  element: string,
  value: string,
  skeleton: skeletonElement
): void {
  const target = skeleton.querySelector<HTMLElement>(element);
  if (target) {
    value ? (target.dataset.target = value) : target.remove();
  }
}

function addToWrapper(
  items: CardData[],
  skeleton: HTMLElement,
  wrapper: HTMLElement
): void {
  wrapper.innerHTML = '';
  items.forEach((el: CardData) => {
    createCard(el, skeleton, wrapper);
  });
}

function appendToWrapper(
  items: CardData[],
  skeleton: HTMLElement,
  wrapper: HTMLElement,
  sliceStart: number
): void {
  const visibleItems = sliceStart + items.length;

  items.forEach((el: CardData) => {
    createCard(el, skeleton, wrapper);
  });

  wrapper.dataset.visibleItems = String(visibleItems);
}

function createCard(
  el: CardData,
  skeleton: HTMLElement,
  wrapper: HTMLElement
): void {
  const card = skeleton.cloneNode(true) as HTMLElement;
  wrapper.appendChild(card);

  elementControl('.js-title', el.title ?? '', card);
  elementControl('.js-subtitle', el.subtitle ?? '', card);

  if (el.summary) {
    elementControl('.js-paragraph', el.summary ?? '', card);
  } else {
    if (el.text) {
      el.text = el.text.replace(/(?:\r\n|\r|\n)/g, '<br>');
    }

    elementControl('.js-paragraph', el.text ?? '', card);
  }
  listControl('.js-badge', el.category ?? [], card);

  imageControl('.js-img--image', el.image ?? '', card, el.title ?? false);

  if (el.modal_target) {
    modalControl('.js-sd-modal__button', el.modal_target, card);
  } else {
    linkControl('.js-cta', el.link ?? '', card);
    linkControl('.js-card__wrapper-link', el.link ?? '', card);
  }

  card.offsetHeight;
  card.classList.add('card__image--visible');
}

export {
  elementControl,
  imageControl,
  linkControl,
  modalControl,
  addToWrapper,
  appendToWrapper,
  createCard
};
